import { useEffect, useMemo, useState } from "react";
import { MapPin } from "lucide-react";
import { destinationCategories, destinations, type DestinationCategory } from "@/data/resort";
import { cn } from "@/lib/utils";
import { Eyebrow, MaskReveal, Reveal } from "../reveal";

const categoryTone: Record<string, string> = {
  "Local Auburn": "bg-forest",
  Outdoors: "bg-river",
  "Gold Country": "bg-gold",
  "Food & Drink": "bg-accent",
  Mountains: "bg-forest-deep",
  "Day Trips": "bg-charcoal",
};

export function DestinationExplorer() {
  const [category, setCategory] = useState<DestinationCategory>("All");
  const [activeId, setActiveId] = useState<string>(destinations[0]?.id ?? "");

  useEffect(() => {
    const onFilter = (event: Event) => {
      const detail = (event as CustomEvent<{ category: DestinationCategory }>).detail;
      if (detail?.category) setCategory(detail.category);
    };
    window.addEventListener("nearby-filter", onFilter);
    return () => window.removeEventListener("nearby-filter", onFilter);
  }, []);

  const visible = useMemo(
    () => (category === "All" ? destinations : destinations.filter((d) => d.category === category)),
    [category],
  );

  useEffect(() => {
    if (!visible.some((d) => d.id === activeId)) {
      setActiveId(visible[0]?.id ?? "");
    }
  }, [visible, activeId]);

  const active = visible.find((d) => d.id === activeId) ?? visible[0];

  return (
    <section
      id="destination-explorer"
      aria-labelledby="explorer-heading"
      className="scroll-mt-16 bg-background py-16 md:py-20"
    >
      <div className="shell">
        <Reveal>
          <Eyebrow>Choose Your Adventure</Eyebrow>
        </Reveal>
        <h2 id="explorer-heading" className="display-lg mt-6 max-w-[18ch]">
          <MaskReveal>So Much to Explore.</MaskReveal>
          <MaskReveal delay={0.08}>One Perfect Base Camp.</MaskReveal>
        </h2>

        <div className="mt-10">
          <p className="eyebrow text-muted-foreground">
            What Kind of Adventure Are You Looking For?
          </p>
          <div
            role="tablist"
            aria-label="Destination categories"
            className="mt-5 flex snap-x gap-2 overflow-x-auto border-y border-border py-5 [scrollbar-width:none] md:flex-wrap md:overflow-visible"
          >
            {destinationCategories.map((cat) => (
              <button
                key={cat}
                type="button"
                role="tab"
                aria-selected={category === cat}
                onClick={() => setCategory(cat)}
                className={cn(
                  "eyebrow shrink-0 snap-start rounded-sm px-4 py-2.5 transition-colors",
                  category === cat
                    ? "bg-primary text-primary-foreground"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground",
                )}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-8 grid gap-10 lg:grid-cols-12">
          <div className="order-1 lg:col-span-7 lg:order-none">
            <div className="sticky top-28 overflow-hidden border border-border bg-secondary/40">
              <div
                aria-label="Interactive destination map placeholder centered on Auburn RV Resort"
                className="relative aspect-[5/4] w-full bg-[radial-gradient(circle_at_40%_55%,oklch(0.9_0.03_85),oklch(0.84_0.03_140)_45%,oklch(0.78_0.04_155)_100%)]"
              >
                <div className="absolute inset-8 rounded-sm border border-forest/15" />
                <div className="absolute left-[18%] top-[22%] h-24 w-32 rounded-full bg-river/20 blur-2xl" />
                <div className="absolute bottom-[18%] right-[16%] h-28 w-36 rounded-full bg-forest/15 blur-2xl" />

                {visible.map((dest, i) => {
                  const left = 18 + ((i * 17) % 62);
                  const top = 16 + ((i * 23) % 58);
                  const activePin = dest.id === active?.id;
                  return (
                    <button
                      key={dest.id}
                      type="button"
                      onClick={() => setActiveId(dest.id)}
                      aria-label={`${dest.name}, ${dest.category}`}
                      aria-pressed={activePin}
                      className={cn(
                        "absolute flex -translate-x-1/2 -translate-y-full flex-col items-center transition-transform duration-300",
                        activePin ? "z-20 scale-110" : "z-10 hover:scale-105",
                      )}
                      style={{ left: `${left}%`, top: `${top}%` }}
                    >
                      <span
                        className={cn(
                          "size-3 rounded-full border-2 border-background shadow-soft",
                          categoryTone[dest.category] ?? "bg-primary",
                        )}
                      />
                      <span
                        className={cn(
                          "mt-1 max-w-[9rem] truncate px-2 py-1 text-[0.65rem] uppercase tracking-[0.14em]",
                          activePin
                            ? "bg-primary text-primary-foreground"
                            : "bg-background/90 text-foreground",
                        )}
                      >
                        {dest.name}
                      </span>
                    </button>
                  );
                })}

                <div className="absolute left-1/2 top-[48%] z-30 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center">
                  <MapPin className="size-8 text-primary drop-shadow" strokeWidth={1.6} />
                  <span className="eyebrow mt-2 bg-background px-3 py-1.5 text-foreground shadow-soft">
                    Base Camp
                  </span>
                </div>
              </div>

              <div className="border-t border-border bg-background p-5 md:p-6">
                <p className="eyebrow text-muted-foreground">Selected Destination</p>
                <p className="font-display mt-2 text-2xl tracking-tight">{active?.name}</p>
                <p className="mt-1 text-sm text-muted-foreground">
                  {active?.category} · {active?.location}
                </p>
                <p className="mt-4 text-xs text-muted-foreground">
                  Distances and drive times are not shown here. Confirm current directions, hours
                  and conditions before you go.
                </p>
              </div>
            </div>
          </div>

          <ul className="order-2 grid gap-5 sm:grid-cols-2 lg:col-span-5 lg:order-none lg:grid-cols-1">
            {visible.map((dest, i) => {
              const selected = dest.id === active?.id;
              return (
                <li key={dest.id} id={`destination-${dest.id}`}>
                  <Reveal delay={Math.min(i, 6) * 0.04}>
                    <article
                      className={cn(
                        "group overflow-hidden border bg-card transition-all duration-500",
                        selected ? "border-primary shadow-soft" : "border-border hover:shadow-soft",
                      )}
                    >
                      <button
                        type="button"
                        className="w-full text-left"
                        onClick={() => setActiveId(dest.id)}
                        aria-pressed={selected}
                      >
                        <div className="overflow-hidden">
                          <img
                            src={dest.image}
                            alt={dest.alt}
                            width={1200}
                            height={900}
                            loading="lazy"
                            decoding="async"
                            className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                          />
                        </div>
                        <div className="p-6">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="eyebrow text-muted-foreground">{dest.category}</span>
                            <span className="text-xs text-muted-foreground">· {dest.location}</span>
                          </div>
                          {dest.headline && (
                            <p className="eyebrow mt-3 text-primary">{dest.headline}</p>
                          )}
                          <h3 className="font-display mt-2 text-2xl tracking-tight">{dest.name}</h3>
                          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                            {dest.description}
                          </p>
                          {dest.note && (
                            <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
                              {dest.note}
                            </p>
                          )}
                          <ul className="mt-4 flex flex-wrap gap-2">
                            {dest.tags.map((tag) => (
                              <li
                                key={tag}
                                className="border border-border px-2.5 py-1 text-[0.65rem] uppercase tracking-[0.14em] text-muted-foreground"
                              >
                                {tag}
                              </li>
                            ))}
                          </ul>
                          <span className="eyebrow mt-5 inline-flex text-primary">{dest.cta}</span>
                        </div>
                      </button>
                    </article>
                  </Reveal>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
}
